import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownContentProps {
  children: string;
}

const MarkdownContent = ({ children }: MarkdownContentProps) => {
  return (
    <div className="prose-blog">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-3xl md:text-4xl font-bold mt-12 mb-6 gradient-text">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-2xl md:text-3xl font-bold mt-10 mb-4 text-foreground">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-xl md:text-2xl font-semibold mt-8 mb-3 text-foreground">{children}</h3>
          ),
          p: ({ children }) => (
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 mb-5">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-outside ml-6 mb-5 space-y-2 text-foreground/80">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside ml-6 mb-5 space-y-2 text-foreground/80">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith("http") ? "_blank" : undefined}
              rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
              className="text-primary underline underline-offset-4 hover:text-accent transition-colors"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/60 pl-5 my-6 italic text-foreground/85 bg-primary/5 py-3 rounded-r">
              {children}
            </blockquote>
          ),
          code: ({ children, className }) => {
            const isBlock = className?.includes("language-");
            if (isBlock) {
              return (
                <code className="block bg-foreground/95 text-background p-4 rounded-lg overflow-x-auto text-sm font-mono my-5">
                  {children}
                </code>
              );
            }
            return (
              <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-sm text-sm font-mono">{children}</code>
            );
          },
          img: ({ src, alt }) => (
            <img src={src} alt={alt ?? ""} className="rounded-lg my-6 w-full" loading="lazy" />
          ),
          hr: () => <hr className="my-10 border-border/60" />,
          table: ({ children }) => (
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-border/60 bg-primary/10 px-3 py-2 text-left font-semibold">{children}</th>
          ),
          td: ({ children }) => <td className="border border-border/60 px-3 py-2">{children}</td>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
